import type { UseFormReturn } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

const personalInfoFormSchema = z
  .object({
    companyName: z
      .string()
      .min(1, "Please fill in this field.")
      .min(2, "Company name must be at least 2 characters."),
    creativeSupportOther: z.string().optional(),
    creativeSupportType: z
      .string()
      .min(1, "Please select a type of creative support."),
    email: z
      .string()
      .min(1, "Please fill in this field.")
      .email("Invalid email address"),
    fullName: z
      .string()
      .min(1, "Please fill in this field.")
      .min(2, "Full name must be at least 2 characters."),
    industry: z.string().min(1, "Please select an industry."),
    industryOther: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.creativeSupportType === "Other") {
        return (
          !!data.creativeSupportOther &&
          data.creativeSupportOther.trim().length > 0
        );
      }

      return true;
    },
    {
      message: "Please specify the type of creative support.",
      path: ["creativeSupportOther"],
    },
  )
  .refine(
    (data) => {
      if (data.industry === "Other") {
        return !!data.industryOther && data.industryOther.trim().length > 0;
      }

      return true;
    },
    {
      message: "Please specify your industry.",
      path: ["industryOther"],
    },
  );

export type PersonalInfoFormData = z.infer<typeof personalInfoFormSchema>;

export function usePersonalInfoForm(
  initialData: Partial<PersonalInfoFormData>,
): UseFormReturn<PersonalInfoFormData> {
  return useForm<PersonalInfoFormData>({
    defaultValues: {
      companyName: initialData.companyName || "",
      creativeSupportOther: initialData.creativeSupportOther || "",
      creativeSupportType: initialData.creativeSupportType || "",
      email: initialData.email || "",
      fullName: initialData.fullName || "",
      industry: initialData.industry || "",
      industryOther: initialData.industryOther || "",
    },
    mode: "onChange",
    resolver: zodResolver(personalInfoFormSchema),
  });
}
